import { useEffect } from 'react';
import { useAnalyticsSelection } from 'app/modules/analytics/hooks/useAnalyticsSelection';
import { useAnalyticsInventory } from 'app/modules/analytics/hooks/useAnalyticsInventory';
import { useAnalyticsHealthQuery } from 'app/modules/analytics/hooks/useAnalyticsHealthQuery';
import { useAnalyticsAutoSyncMemory } from 'app/modules/analytics/hooks/useAnalyticsAutoSyncMemory';
import { computeAnalyticsAutoSyncSelectionKey } from 'app/modules/analytics/utils/analyticsAutoSyncSelectionKey';
import { isPageHealthTrackingOff } from 'app/modules/analytics/utils/analyticsContentHeaderSync';
import { doesAnalyticsSyncSelectionRequireReconnect } from 'app/modules/analytics/utils/analyticsSyncReconnect';

interface AnalyticsAutoSyncOrchestrationParams {
  requestSync: () => void;
  isSyncing: boolean;
  isShowingSuccess: boolean;
}

/**
 * Selection-driven auto-sync decision, owned by the single `AnalyticsSyncDriver`
 * (not by any tab). Auto-sync starts once for a selection that has never been
 * synced — a single page whose health has no `last_successful_sync_at`, or a
 * group whose every enabled page has none. A selection with any sync date is
 * left to the manual sync button. Because this lives in the driver, tab
 * navigation never triggers a sync; only changing the selection re-evaluates.
 */
export function useAnalyticsAutoSyncOrchestration({
  requestSync,
  isSyncing,
  isShowingSuccess,
}: AnalyticsAutoSyncOrchestrationParams): void {
  const { isLoading } = useAnalyticsInventory();
  const {
    selectedPage,
    selectedPageId,
    selectedGroupId,
    isGroupSelected,
    enabledGroupPageIds,
  } = useAnalyticsSelection();
  const {
    getPageHealth,
    isLoading: isHealthLoading,
    healthByPageId,
  } = useAnalyticsHealthQuery();

  const selectionKey = computeAnalyticsAutoSyncSelectionKey({
    isGroupSelected,
    selectedGroupId,
    selectedPageId,
    enabledGroupPageIds,
  });

  useEffect(() => {
    if (isLoading) return;
    if (selectionKey == null) return;
    if (isHealthLoading) return;
    if (isSyncing || isShowingSuccess) return;

    const autoSyncMemory = useAnalyticsAutoSyncMemory.getState();
    if (autoSyncMemory.hasIssuedSyncForSelectionKey(selectionKey)) return;
    if (autoSyncMemory.hasDismissedAutoSyncForSelectionKey(selectionKey))
      return;

    if (
      doesAnalyticsSyncSelectionRequireReconnect({
        isGroupSelected,
        selectedPage,
        selectedPageId,
        enabledGroupPageIds,
        getPageHealth,
      })
    ) {
      return;
    }

    if (isGroupSelected) {
      const pageIds = Array.from(enabledGroupPageIds);
      if (pageIds.length === 0) return;
      const healthList = pageIds.map((pageId) => getPageHealth(pageId));
      if (healthList.some((health) => health == null)) return;
      if (healthList.every((health) => isPageHealthTrackingOff(health))) return;
      const groupNeverSynced = healthList.every(
        (health) => !health?.last_successful_sync_at,
      );
      if (!groupNeverSynced) return;
    } else {
      if (selectedPageId == null) return;
      const selectedPageHealth = getPageHealth(selectedPageId);
      if (selectedPageHealth == null) return;
      if (isPageHealthTrackingOff(selectedPageHealth)) return;
      if (selectedPageHealth.last_successful_sync_at) return;
    }

    autoSyncMemory.markIssuedSyncForSelectionKey(selectionKey);
    requestSync();
  }, [
    isLoading,
    selectionKey,
    isHealthLoading,
    isSyncing,
    isShowingSuccess,
    isGroupSelected,
    selectedPage,
    selectedPageId,
    enabledGroupPageIds,
    getPageHealth,
    healthByPageId,
    requestSync,
  ]);
}
